/**
 * Project Bobcat — the solution assistant's pure rules (`bobcat-solution-
 * assistant`). Kept free of the table store so they test in node.
 */

import type { NodeProposal } from './node-proposals';

/**
 * PSTUDIO-4632: whether Apply (and Undo) may write now. A viewer never may;
 * an editor may not while the table is not writable — mid-publish above all,
 * where the write would unlock the table the publish locked. `locked` keeps
 * the card and its button, disabled; `view-only` replaces the button.
 */
export type AssistantWriteGate = 'open' | 'locked' | 'view-only';

export const assistantWriteGate = (store: {
  isViewer: boolean;
  canWriteRows: boolean;
}): AssistantWriteGate =>
  store.isViewer ? 'view-only' : store.canWriteRows ? 'open' : 'locked';

export interface AssistantTurn {
  who: 'me' | 'ai';
  text: string;
  /** Older answers: the question asked for a node to be written. */
  suggestBuild?: boolean;
  error?: boolean;
  /** Proposed node content, validated against the solution, for review. */
  proposals?: NodeProposal[];
  /** Why parts of a proposal were left out. */
  notes?: string[];
  /** Row ids whose proposal the designer applied. */
  appliedRowIds?: string[];
  /** A new node's placeholder row id → the row Apply created for it. */
  createdRows?: Record<string, string>;
  /** Per proposal (row id), the fields the designer dropped from it. */
  keeps?: Record<string, Record<string, boolean>>;
  /** Agent-setting changes, with the value each one replaces. */
  settingChanges?: AssistantSettingChange[];
  /** What happened to `settingChanges`: applied (undoable) or discarded. */
  settingsStatus?: 'applied' | 'discarded';
  /** A question it asked instead of guessing; answered by the next message. */
  ask?: { question: string; why: string; options: string[] };
}

export interface AssistantSettingChange {
  agent: 'supervisor' | 'knowledge';
  key: string;
  from: string | boolean;
  to: string | boolean;
  /** Left out of the apply by the designer. */
  off?: boolean;
}

/**
 * The node the next question is about: the selected node, unless the
 * designer cleared it for that same selection ("About node N ×"). Selecting a
 * different node brings the context back.
 */
export const assistantContextRowId = (
  selectedRowId: string | null,
  clearedRowId: string | null
): string | null =>
  selectedRowId && selectedRowId !== clearedRowId ? selectedRowId : null;

export const assistantGreeting = (nodeNumber: string | null): string =>
  nodeNumber
    ? `Ask me about this solution, or ask me to fill in node ${nodeNumber} — you review every change before it's applied.`
    : "Ask me about this solution, or ask me to fill in a node — you review every change before it's applied.";

export const assistantStarters = (nodeNumber: string | null): string[] =>
  nodeNumber
    ? [
        `What does node ${nodeNumber} do?`,
        `Fill in node ${nodeNumber}`,
        `What routes into node ${nodeNumber}?`,
      ]
    : [
        'What does this solution do?',
        'Which agents does the Supervisor route to?',
        'Where is a variable set?',
      ];

/** The last few turns, as the chat route's `history`. The greeting is the
 *  assistant's own text, not a model turn, so it is left out. */
export const assistantHistory = (
  turns: AssistantTurn[]
): { who: 'me' | 'ai'; text: string }[] =>
  turns.filter((t) => !t.error).map(({ who, text }) => ({ who, text }));
